import React, { useState, Component } from 'react';
/**
 * ExplanationContent — Conversational explanation with Djay intros.
 *
 * Shows distractor explanation (why wrong) and main explanation (how to solve)
 * with Djay avatar intros and numbered solution steps.
 *
 * Co-located in practice/ domain (CL-1 s64).
 */
import { motion } from 'framer-motion';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { DjayAvatar, useDjaySelection, getDjayDistractorIntro, getDjayExplanationIntro } from '../../../mascot';
// ── Sub-component ────────────────────────────────────────────
function ExplanationSteps({
  text,
  steps
}: {
  text?: string;
  steps?: string[];
}) {
  const stepsToRender = steps || (text ? text.split('\n').filter(line => line.trim().length > 0) : []);
  if (stepsToRender.length <= 1 && text) {
    return <div className="text-sm md:text-base leading-relaxed text-slate-700 font-medium" data-id="element-6612">
        <LatexRenderer content={text} data-id="element-6613" />
      </div>;
  }
  return <div className="space-y-3 relative" data-id="element-6614">
      <div className="absolute left-3 top-4 bottom-4 w-0.5 bg-gradient-to-b from-orange-200 via-orange-300 to-orange-200 rounded-full" data-id="element-6615" />
      {stepsToRender.map((step, idx) => <motion.div key={idx} initial={{
      opacity: 0,
      x: -10
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.08 * idx,
      duration: 0.3,
      ease: 'easeOut'
    }} className="flex gap-3 items-start relative" data-id="element-6616">
          <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-black text-sm bg-gradient-to-br from-orange-400 to-amber-500 text-white shadow-md shadow-orange-200/50 z-10" data-id="element-6617">
            {idx + 1}
          </div>
          <div className="flex-1 text-sm md:text-base leading-relaxed pt-0.5 text-slate-700 font-medium bg-white/50 rounded-xl px-3 py-2 border border-slate-100" data-id="element-6618">
            <LatexRenderer content={step} data-id="element-6619" />
          </div>
        </motion.div>)}
    </div>;
}
// ── Types ────────────────────────────────────────────────────
interface ExplanationContentProps {
  explanation: string;
  distractorExplanation?: string;
  isCorrect: boolean;
  solutionSteps?: string[];
}
// ── Component ────────────────────────────────────────────────
export function ExplanationContent({
  explanation,
  distractorExplanation,
  isCorrect,
  solutionSteps
}: ExplanationContentProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  // useState initializer runs once — prevents random re-pick on scroll/re-render
  const [distractorIntro] = useState(() => getDjayDistractorIntro());
  const [explanationIntro] = useState(() => getDjayExplanationIntro());
  return <div className="space-y-4" data-id="element-6620">
      {/* Distractor explanation (why it was wrong) */}
      {!isCorrect && distractorExplanation && <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} className="bg-gradient-to-br from-slate-50 to-slate-100/80 rounded-2xl p-4 border border-slate-200/80 shadow-sm" data-id="element-6621">
          <div className="flex items-start gap-3" data-id="element-6622">
            <motion.div className="flex-shrink-0 mt-0.5" animate={{
          rotate: [0, -5, 5, 0]
        }} transition={{
          delay: 0.3,
          duration: 0.5
        }} data-id="element-6623">
              <DjayAvatar size="sm" emotion="thinking" variant={selectedVariant} animate={true} data-id="element-6624" />
            </motion.div>
            <div className="min-w-0" data-id="element-6625">
              <h4 className="font-bold text-slate-700 text-sm mb-1.5" data-id="element-6626">
                {distractorIntro}
              </h4>
              <div className="text-slate-600 text-sm leading-relaxed" data-id="element-6627">
                <LatexRenderer content={distractorExplanation} data-id="element-6628" />
              </div>
            </div>
          </div>
        </motion.div>}

      {/* Main explanation (how to solve it) */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: distractorExplanation ? 0.15 : 0,
      duration: 0.3
    }} className="relative" data-id="element-6629">
        <div className="flex items-center gap-2 mb-3" data-id="element-6630">
          <motion.div animate={{
          y: [0, -2, 0]
        }} transition={{
          delay: 0.4,
          duration: 1.5,
          repeat: Infinity,
          repeatType: 'reverse'
        }} data-id="element-6631">
            <DjayAvatar size="sm" emotion="pointing" variant={selectedVariant} animate={true} data-id="element-6632" />
          </motion.div>
          <span className="text-sm font-bold text-orange-600" data-id="element-6633">
            {explanationIntro}
          </span>
        </div>

        <ExplanationSteps text={explanation || (isCorrect ? 'Goed gedaan! Je hebt de juiste strategie gebruikt.' : 'Kijk goed naar de stappen hierboven.')} steps={solutionSteps} data-id="element-6634" />
      </motion.div>
    </div>;
}